import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchData } from '../../hooks/useSearchData';
import SearchDropdown from './SearchDropdown';

function AppSearch({
  value,
  onChange,
  placeholder = 'Search projects, reviews, tasks...',
  shortcutHint = '⌘ K',
  className = '',
}) {
  const navigate = useNavigate();
  const { searchItems, loading } = useSearchData();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filteredResults, setFilteredResults] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search filter
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!value.trim()) {
      setFilteredResults([]);
      setDropdownOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const searchTerm = value.toLowerCase();
      
      // Filter across all searchable fields
      const filtered = searchItems.filter((item) =>
        item.searchableText.includes(searchTerm)
      );

      // Group by type and take top 5 of each
      const grouped = {};
      filtered.forEach((item) => {
        if (!grouped[item.type]) {
          grouped[item.type] = [];
        }
        if (grouped[item.type].length < 5) {
          grouped[item.type].push(item);
        }
      });

      // Flatten grouped results
      const results = Object.values(grouped).flat();
      setFilteredResults(results);
      setDropdownOpen(results.length > 0);
      setSelectedIndex(0);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, searchItems]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (!dropdownOpen || filteredResults.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredResults.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredResults.length) % filteredResults.length);
        break;
      case 'Enter':
        event.preventDefault();
        if (filteredResults[selectedIndex]) {
          navigate(filteredResults[selectedIndex].route);
          setDropdownOpen(false);
          onChange('');
        }
        break;
      case 'Escape':
        event.preventDefault();
        setDropdownOpen(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  }, [dropdownOpen, filteredResults, selectedIndex, onChange, navigate]);

  // Global keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleGlobalShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleGlobalShortcut);
    return () => document.removeEventListener('keydown', handleGlobalShortcut);
  }, []);

  const handleInputChange = (newValue) => {
    onChange(newValue);
  };

  const handleResultSelect = (result) => {
    setDropdownOpen(false);
    onChange('');
  };

  const handleCloseDropdown = () => {
    setDropdownOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <label
        className="flex items-center gap-3 h-10 px-3 rounded-lg border border-gray-700 bg-gray-900/70 text-gray-300 focus-within:ring-2 focus-within:ring-axial-500/40 focus-within:border-axial-500 w-full"
      >
        <span aria-hidden="true" className="text-gray-500 text-xs">SRCH</span>
        <input
          ref={inputRef}
          type="search"
          value={value}
          onChange={(event) => handleInputChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full bg-transparent text-sm text-gray-100 placeholder-gray-500 outline-none"
        />
        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 border border-gray-600 rounded px-2.5 py-0.5 whitespace-nowrap">
          {shortcutHint}
        </span>
      </label>

      {dropdownOpen && !loading && (
        <SearchDropdown
          results={filteredResults}
          selectedIndex={selectedIndex}
          onSelect={handleResultSelect}
          onClose={handleCloseDropdown}
        />
      )}
    </div>
  );
}

export default AppSearch;
